import { IconButton, styled, Theme } from "@mui/material";
import { IStyledIconButtonProps } from "./types";

const pickColor = (theme?: Theme) => ({
    primary: (theme as any).palette?.primary?.darker,
    secondary: (theme as any).palette?.secondary?.main,
    tertiary: (theme as any).palette?.tertiary?.main,
    quaternary: (theme as any).palette?.primary?.main,
    neutral: (theme as any).palette?.neutral?.lightest,
    accentInfo: (theme as any).palette?.accentInfo?.main,
    accentSuccess: (theme as any).palette?.accentSuccess?.main,
    accentWarning: (theme as any).palette?.accentWarning?.main,
    accentError: (theme as any).palette?.accentError?.main,
})

const StyledIconButton = styled(IconButton,{
    shouldForwardProp: prop => !["customPosition", "customVariant", "customColor"].includes(prop.toString())
})<IStyledIconButtonProps>(({ theme, customPosition, customVariant, customColor = "primary" }) => {
    // basic visual styles
    // "filled": solid background, light icon; both flip on hover.
    // "ghost": no background (blends into the surrounding UI), icon already in its final color; hover only reveals a background behind it, the icon color stays the same.
    // "clean": never has a background or a hover state — just the icon.
    const isFilled = customVariant === 'filled';
    const isGhost = customVariant === 'ghost';

    const baseStyles = {
        backgroundColor: `${isFilled ? (theme as any).palette?.primary?.darker : 'transparent'}`,
        borderRadius: `${customVariant === 'clean' ? '50%' : (theme as any).shape?.border?.button?.radius?.[1]}`,
        padding: `${customVariant === "clean" ? "0px" : isGhost ? theme.spacing(0.5) : theme.spacing(1)}`,
        "& .MuiSvgIcon-root":{
            color: `${isFilled ? (theme as any).palette.neutral.lighter : pickColor(theme)[customColor]} !important`,
        },
        "&:hover":{
            backgroundColor: `${isFilled || isGhost ? (theme as any).palette?.neutral?.lighter : 'transparent'}`,
            "& .MuiSvgIcon-root":{
            color: `${isFilled ? (theme as any).palette.primary.darker : pickColor(theme)[customColor]} !important`,
        },
        },
        '&.Mui-disabled':{
            border: `1px solid ${(theme as any).palette.neutral.dark}`,
            color: `${(theme as any).palette.neutral.dark} !important`
        }
    } as any;

    // positioning logic: absolute relative to parent.
    // small offset (8px) from the edges to avoid sticking flush; change if needed.
    const offset = 8;
    const positionStyles: Record<string, any> = {};

    if (customPosition) {
        positionStyles.position = 'absolute';
        switch (customPosition) {
            case 'top-left':
                positionStyles.top = offset;
                positionStyles.left = offset;
                break;
            case 'top-right':
                positionStyles.top = offset;
                positionStyles.right = offset;
                break;
            case 'bottom-left':
                positionStyles.bottom = offset;
                positionStyles.left = offset;
                break;
            case 'bottom-right':
                positionStyles.bottom = offset;
                positionStyles.right = offset;
                break;
            default:
                break;
        }
    }

    return {
        ...baseStyles,
        ...positionStyles
    };
});

export { StyledIconButton }